import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Tag, Search, ArrowRight, MapPin, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import { useGooglePlaces, type Prediction } from "@/hooks/useGooglePlaces";
import { motion } from "framer-motion";
import { toast } from "sonner";

const BookingWidget = () => {
  const navigate = useNavigate();
  const [routeType, setRouteType] = useState<"one-way" | "round-trip">("one-way");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [fromPlaceId, setFromPlaceId] = useState("");
  const [toPlaceId, setToPlaceId] = useState("");
  const [passengers, setPassengers] = useState("2");
  const [promoCode, setPromoCode] = useState("");

  const fromPlaces = useGooglePlaces();
  const toPlaces = useGooglePlaces();

  const isAirportRoute = fromPlaceId.startsWith("db-airport-") || toPlaceId.startsWith("db-airport-");
  const maxPassengers = isAirportRoute ? 50 : 10;

  useEffect(() => {
    if (parseInt(passengers) > maxPassengers) {
      setPassengers(String(maxPassengers));
    }
  }, [maxPassengers]);

  const handleFromSelect = (prediction: Prediction) => {
    setFrom(prediction.description);
    setFromPlaceId(prediction.place_id);
    fromPlaces.setPredictions([]);
  };

  const handleToSelect = (prediction: Prediction) => {
    setTo(prediction.description);
    setToPlaceId(prediction.place_id);
    toPlaces.setPredictions([]);
  };

  // If user edits the text manually after selecting, invalidate the placeId
  const handleFromChange = (val: string) => {
    setFrom(val);
    if (fromPlaceId) setFromPlaceId("");
  };
  const handleToChange = (val: string) => {
    setTo(val);
    if (toPlaceId) setToPlaceId("");
  };

  const fromInvalid = from.trim().length > 0 && !fromPlaceId;
  const toInvalid = to.trim().length > 0 && !toPlaceId;
  const canSearch = !!fromPlaceId && !!toPlaceId;

  const handleSearch = () => {
    if (!canSearch) {
      toast.error("Please select a pick-up and drop-off from the suggestions list.", {
        description: "Free text is not allowed — choose a hotel or airport from the dropdown so we can quote the right price.",
      });
      return;
    }
    navigate("/vehicles", {
      state: {
        from, to, fromPlaceId, toPlaceId, routeType,
        passengers: parseInt(passengers),
        promoCode,
      },
    });
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: (i: number) => ({
      opacity: 1, y: 0,
      transition: { delay: i * 0.08, duration: 0.4, ease: [0, 0, 0.2, 1] as const }
    }),
  };

  return (
    <div className="w-full">
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="bg-card rounded-3xl p-7 md:p-10 shadow-[0_25px_80px_-15px_rgba(0,82,204,0.25)] border border-border/50 backdrop-blur-sm"
      >
        {/* Header */}
        <motion.div custom={0} variants={itemVariants} initial="hidden" animate="visible">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-foreground mb-1 tracking-tight">
            Book Your <span className="text-gradient-caribbean">Transfer</span>
          </h2>
          <p className="text-muted-foreground font-sans text-sm mb-7">
            Enter your trip details below
          </p>
        </motion.div>

        {/* Route Type Pill Toggle */}
        <motion.div custom={1} variants={itemVariants} initial="hidden" animate="visible" className="mb-7">
          <div className="inline-flex bg-muted rounded-2xl p-1.5 gap-1">
            <button
              onClick={() => setRouteType("one-way")}
              className={`relative px-6 py-2.5 rounded-xl text-sm font-bold font-sans transition-all duration-300 ${
                routeType === "one-way"
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                  : "text-foreground hover:text-foreground"
              }`}
            >
              One Way
            </button>
            <button
              onClick={() => setRouteType("round-trip")}
              className={`relative px-6 py-2.5 rounded-xl text-sm font-bold font-sans transition-all duration-300 ${
                routeType === "round-trip"
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                  : "text-foreground hover:text-foreground"
              }`}
            >
              Round Trip
            </button>
          </div>
        </motion.div>

        {/* Route Inputs with Visual Connector */}
        <motion.div custom={2} variants={itemVariants} initial="hidden" animate="visible" className="relative mb-5">
          <div className="space-y-3">
            {/* From */}
            <div className="relative">
              <div className="absolute left-4 top-[1.75rem] -translate-y-1/2 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center z-10">
                <MapPin className="w-5 h-5 text-primary" />
              </div>
              <PlaceAutocomplete
                placeholder="Pick-up: Airport, Hotel or Address"
                value={from}
                onChange={handleFromChange}
                onSelect={handleFromSelect}
                predictions={fromPlaces.predictions}
                loading={fromPlaces.loading}
                onSearch={fromPlaces.search}
                iconColor="hidden"
                invalid={fromInvalid}
              />
              {fromInvalid && (
                <p className="mt-1.5 ml-1 text-xs text-destructive flex items-center gap-1.5 font-sans">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Please choose an option from the list
                </p>
              )}
            </div>

            {/* Route connector line */}
            <div className="absolute left-[2.15rem] top-[3.2rem] bottom-[calc(100%-5.8rem)] w-px z-10">
              <div className="h-full border-l-2 border-dashed border-primary/20" />
            </div>

            {/* To */}
            <div className="relative">
              <div className="absolute left-4 top-[1.75rem] -translate-y-1/2 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center z-10">
                <MapPin className="w-5 h-5 text-accent" />
              </div>
              <PlaceAutocomplete
                placeholder="Drop-off: Hotel, Address or Airport"
                value={to}
                onChange={handleToChange}
                onSelect={handleToSelect}
                predictions={toPlaces.predictions}
                loading={toPlaces.loading}
                onSearch={toPlaces.search}
                iconColor="hidden"
                invalid={toInvalid}
              />
              {toInvalid && (
                <p className="mt-1.5 ml-1 text-xs text-destructive flex items-center gap-1.5 font-sans">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Please choose an option from the list
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Passengers & Promo */}
        <motion.div custom={3} variants={itemVariants} initial="hidden" animate="visible" className="grid grid-cols-2 gap-3 mb-7">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center z-10">
              <Users className="w-4 h-4 text-primary" />
            </div>
            <Select value={passengers} onValueChange={setPassengers}>
              <SelectTrigger aria-label="Number of passengers" className="pl-14 h-14 bg-card border-border rounded-xl text-foreground text-sm font-medium hover:border-primary/40 transition-colors">
                <SelectValue placeholder="Passengers" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: maxPassengers }, (_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {i + 1} {i === 0 ? "Passenger" : "Passengers"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-secondary/10 flex items-center justify-center z-10">
              <Tag className="w-4 h-4 text-secondary" />
            </div>
            <input
              aria-label="Promo code"
              placeholder="Promo Code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="flex h-14 w-full rounded-xl border border-border bg-card px-3 pl-14 py-2 text-sm font-medium text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0 hover:border-primary/40 transition-colors"
            />
          </div>
        </motion.div>

        {/* Search Button */}
        <motion.div custom={4} variants={itemVariants} initial="hidden" animate="visible">
          <Button
            onClick={handleSearch}
            disabled={!canSearch}
            size="lg"
            className="w-full h-14 text-base font-sans font-bold gap-2 rounded-2xl bg-gradient-caribbean hover:opacity-95 shadow-[0_8px_30px_-5px_hsl(var(--primary)/0.4)] hover:shadow-[0_12px_40px_-5px_hsl(var(--primary)/0.5)] hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <Search className="w-5 h-5" />
            Search Available Vehicles
            <ArrowRight className="w-4 h-4" />
          </Button>
          {!canSearch && (from || to) && (
            <p className="mt-3 text-center text-xs text-muted-foreground font-sans">
              Select pick-up and drop-off from the suggestions to see available vehicles and prices.
            </p>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default BookingWidget;
